import React, { useState } from 'react';
import '../styles/app/picks.css';
import '../styles/app/picks-page.css';

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const SUFFIX = /^(jr\.?|sr\.?|ii|iii|iv)$/i;
const splitName = (name) => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first: '', last: parts[0] || '' };
  let cut = parts.length - 1;
  if (SUFFIX.test(parts[cut]) && cut > 1) cut -= 1;
  return { first: parts.slice(0, cut).join(' '), last: parts.slice(cut).join(' ') };
};

const VISIBLE_PICKS_LIMIT = 3;

function AllPicksView({ users, fights, pool }) {
  const [selectedUser, setSelectedUser] = useState(null);
  const [expandedFights, setExpandedFights] = useState(new Set());

  const toggleExpanded = (fightId) => {
    setExpandedFights(prev => {
      const next = new Set(prev);
      if (next.has(fightId)) next.delete(fightId);
      else next.add(fightId);
      return next;
    });
  };

  // Get allowed card types based on pool settings
  const getAllowedCardTypes = () => {
    const inclusion = pool?.fight_inclusion || (pool?.include_all_fights === 0 ? 'main' : 'all');
    switch (inclusion) {
      case 'main':
        return ['main'];
      case 'main_prelims':
        return ['main', 'prelim'];
      case 'all':
      default:
        return ['main', 'prelim', 'early'];
    }
  };

  const allowedCardTypes = getAllowedCardTypes();
  const activeFights = fights.filter(fight => {
    if (fight.is_cancelled) return false;
    return allowedCardTypes.includes(fight.card_type);
  });

  const mainCardFights = activeFights.filter(f => f.card_type === 'main').sort((a, b) => (a.fight_order ?? 0) - (b.fight_order ?? 0));
  const prelimFights = activeFights.filter(f => f.card_type === 'prelim').sort((a, b) => (a.fight_order ?? 0) - (b.fight_order ?? 0));
  const earlyFights = activeFights.filter(f => f.card_type === 'early').sort((a, b) => (a.fight_order ?? 0) - (b.fight_order ?? 0));

  const showPrelims = allowedCardTypes.includes('prelim');
  const showEarlyPrelims = allowedCardTypes.includes('early');

  // Group picks by user
  const userPicksMap = {};
  users.forEach(user => {
    userPicksMap[user.id] = {
      ...user,
      picksMap: {}
    };
    (user.picks || []).forEach(pick => {
      userPicksMap[user.id].picksMap[pick.fight_id] = pick;
    });
  });

  const renderUserChip = ({ user, pick, isCompleted, isDraw }) => {
    const isCorrect = pick?.is_correct;
    const pickedMethod = pick?.predicted_method;

    let chipState = '';
    if (isCompleted && !isDraw) {
      chipState = isCorrect ? 'is-correct' : 'is-wrong';
    }

    return (
      <div key={user.id} className={`pk-ap-chip ${chipState}`}>
        {isCompleted && !isDraw && (
          <span className={`pk-ap-mark ${isCorrect ? 'is-correct' : 'is-wrong'}`}>
            {isCorrect ? '✓' : '✗'}
          </span>
        )}
        {isCompleted && isDraw && (
          <span className="pk-ap-mark is-draw">—</span>
        )}
        <span className="pk-ap-name">{user.username}</span>
        {pickedMethod && isCorrect && (
          <span className={`pk-ap-method ${pick?.method_bonus > 0 ? 'is-bonus' : 'is-miss'}`}>
            {pickedMethod}{pick?.method_bonus > 0 ? ' ✓' : ''}
          </span>
        )}
        {isCompleted && !isDraw && isCorrect && pick.points_earned > 0 && (
          <span className="pk-ap-pts">
            +{pick.points_earned}
          </span>
        )}
      </div>
    );
  };

  const renderFighterInfo = (corner, fighterName, imageUrl, record, odds, isCompleted, isDraw, winner) => {
    const isWinner = !isDraw && fighterName === winner;
    const isLoser = isCompleted && !isDraw && fighterName !== winner;
    const { first, last } = splitName(fighterName);
    const state = isCompleted ? (isDraw ? 'is-drawn' : (isWinner ? 'is-won' : '')) : '';

    return (
      <div className={`pk-corner pk-corner--${corner} ${state} ${isLoser ? 'is-lost' : ''}`}>
        <div className="fighter-photo pk-well">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={fighterName}
              decoding="async"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <span className="pk-initials" style={{ display: imageUrl ? 'none' : 'flex' }}>
            {getInitials(fighterName)}
          </span>
          {isCompleted && isWinner && <span className="pk-win-tag">Winner</span>}
        </div>
        <div className="fighter-name">
          {first && <span className="pk-name-first">{first}</span>}
          {first && ' '}
          <span className="pk-name-last">{last}</span>
        </div>
        {(record || odds) && (
          <div className="pk-tape-stats">
            {record && <span className="fighter-record">{record}</span>}
            {odds && (
              <span className={`pk-odds ${odds.startsWith('-') ? 'is-fav' : 'is-dog'}`}>{odds}</span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderPicksColumn = (corner, pickers, totalWithPicks, otherCount, isCompleted, isDraw, fightId) => {
    const count = pickers.length;
    const isExpanded = expandedFights.has(fightId);
    const visiblePickers = isExpanded ? pickers : pickers.slice(0, VISIBLE_PICKS_LIMIT);
    const hiddenCount = pickers.length - VISIBLE_PICKS_LIMIT;
    const isLead = count >= otherCount && count > 0;

    return (
      <div className="pk-ap-col">
        {totalWithPicks > 0 && (
          <span className={`pk-ap-count is-${corner} ${isLead ? 'is-lead' : ''}`}>
            {count}/{totalWithPicks} picks
          </span>
        )}
        <div className="pk-ap-list">
          {visiblePickers.map(p => renderUserChip({ ...p, isCompleted, isDraw }))}
          {!isExpanded && hiddenCount > 0 && (
            <button type="button" className="pk-ap-more" onClick={() => toggleExpanded(fightId)}>
              +{hiddenCount} more
            </button>
          )}
          {isExpanded && hiddenCount > 0 && (
            <button type="button" className="pk-ap-more is-less" onClick={() => toggleExpanded(fightId)}>
              Show less
            </button>
          )}
          {pickers.length === 0 && (
            <div className="pk-ap-none">
              No picks
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderFightCard = (fight, featured = false) => {
    const winner = fight.winner;
    const isDraw = winner === 'Draw';
    const isCompleted = winner !== null;
    const isTitle = fight.is_title_fight === 1;
    const rounds = fight.rounds ?? (isTitle || fight.is_main_event ? 5 : 3);

    // Group users by who they picked
    const fighter1Pickers = [];
    const fighter2Pickers = [];

    users.forEach(user => {
      const pick = userPicksMap[user.id]?.picksMap[fight.id];
      if (!pick) return;
      if (pick.predicted_winner === fight.fighter1_name) {
        fighter1Pickers.push({ user, pick });
      } else {
        fighter2Pickers.push({ user, pick });
      }
    });

    const totalWithPicks = fighter1Pickers.length + fighter2Pickers.length;

    return (
      <div
        key={fight.id}
        className={`fight-card pk-fc pk-fc--locked ${isTitle ? 'title' : ''} ${featured ? 'pk-fc--featured' : ''} ${isCompleted ? 'is-graded' : ''}`}
      >
        <div className="pk-fc-head">
          <div className="fight-meta">
            {featured && <span className="pk-chip-main">Main Event</span>}
            {isTitle && (
              <span className="fight-badge title-fight">Title Fight</span>
            )}
            <span className="weight-class">{fight.weight_class}</span>
            <span className={`rounds-pill ${isTitle ? 'title' : ''}`}>{rounds} Rounds</span>
          </div>
          {/* Result banner */}
          {isCompleted && (
            <span className={`pk-ap-result ${isDraw ? 'is-draw' : ''}`}>
              {isDraw ? `Result: ${fight.method === 'NC' ? 'NC' : 'Draw'}` : `${winner} wins${fight.method ? ` via ${fight.method}` : ''}`}
            </span>
          )}
        </div>

        {/* Fighter matchup - photos/names/records at same height */}
        <div className="fight-matchup pk-tape">
          {renderFighterInfo('red', fight.fighter1_name, fight.fighter1_image_url, fight.fighter1_record, fight.fighter1_odds, isCompleted, isDraw, winner)}
          <div className="fight-vs pk-vs-slot" aria-hidden="true">
            <span className="pk-vs">VS</span>
          </div>
          {renderFighterInfo('blue', fight.fighter2_name, fight.fighter2_image_url, fight.fighter2_record, fight.fighter2_odds, isCompleted, isDraw, winner)}
        </div>

        {/* Pool split */}
        {totalWithPicks > 0 && (
          <div className="pk-ap-split" aria-hidden="true">
            <div style={{ width: `${(fighter1Pickers.length / totalWithPicks) * 100}%` }} />
            <div style={{ width: `${(fighter2Pickers.length / totalWithPicks) * 100}%` }} />
          </div>
        )}

        {/* Picks section - two columns below the matchup */}
        <div className="pk-ap-cols">
          {renderPicksColumn('red', fighter1Pickers, totalWithPicks, fighter2Pickers.length, isCompleted, isDraw, fight.id)}
          {renderPicksColumn('blue', fighter2Pickers, totalWithPicks, fighter1Pickers.length, isCompleted, isDraw, fight.id)}
        </div>
      </div>
    );
  };

  const renderSection = (title, list, emptyText, withFeatured = false) => (
    <section className="pk-section">
      <div className="pk-section-head">
        <h2 className="pk-section-title">{title}</h2>
      </div>
      {list.length === 0 ? (
        <div className="pk-none">{emptyText}</div>
      ) : (
        <div className="pk-grid">
          {list.map((fight, idx) => renderFightCard(fight, withFeatured && (!!fight.is_main_event || idx === 0)))}
        </div>
      )}
    </section>
  );

  return (
    <div className="pk-ap">
      {/* Summary Card */}
      <div className="card pk-ap-summary">
        <div className="pk-ap-summary-head">
          <h3>All Picks</h3>
          <span>{users.length} members</span>
        </div>
        <div className="pk-ap-users">
          {users.map(user => {
            const picks = user.picks || [];
            const totalPicks = Object.keys(userPicksMap[user.id]?.picksMap || {}).length - picks.filter(p => p.is_draw).length;
            const correctPicks = picks.filter(p => p.is_correct).length;
            const totalPoints = picks.reduce((sum, p) => sum + (p.points_earned || 0), 0);
            const isActive = selectedUser?.id === user.id;

            return (
              <button
                type="button"
                key={user.id}
                onClick={() => setSelectedUser(selectedUser?.id === user.id ? null : user)}
                className={`pk-ap-user ${isActive ? 'is-active' : ''}`}
                aria-pressed={isActive}
              >
                <span className="pk-ap-user-name">
                  {user.username}
                </span>
                <span className="pk-ap-user-line">
                  {correctPicks}/{totalPicks} correct - {totalPoints} pts
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Card Fights */}
      {renderSection('Main Card', mainCardFights, 'No main card fights available.', true)}

      {/* Prelim Fights */}
      {showPrelims && renderSection('Prelims', prelimFights, 'No prelim fights available.')}

      {/* Early Prelim Fights */}
      {showEarlyPrelims && renderSection('Early Prelims', earlyFights, 'No early prelim fights available.')}
    </div>
  );
}

export default AllPicksView;
